import { ComponentProps, useTransition } from 'react'
import { useRouter } from 'next/navigation'
import { useTranslation } from 'react-i18next'
import { HedvigPillow } from '@/components/HedvigPillow/HedvigPillow'
import { PREFILL_FROM_RECOMMENDATION_QUERY_PARAM } from '@/features/priceCalculator/usePrefillFromRecommendation'
import {
  appendPriceIntentAttributionQuery,
  EXTERNAL_INSURANCE_PRODUCT_RECOMMENDATION_USER_FLOW,
} from '@/features/recommendations/priceIntentAttributionStorage'
import { Alert } from 'ui/src/components/Alert/Alert'
import { Button } from 'ui/src/components/Button/Button'
import { Card } from 'ui/src/components/Card/Card'
import { InfoIcon } from 'ui/src/icons/InfoIcon'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { type ProductRecommendation } from '../../hooks/useRecommendations'
import { RecommendationCard } from './RecommendationCard'

type Props = Omit<ComponentProps<typeof RecommendationCard>, 'children'> & {
  recommendation: ProductRecommendation
}

export function RecommendedProduct({
  recommendation,
  isExpanded: externalIsExpanded,
  defaultExpanded = true,
  onExpandedChange,
}: Props) {
  const { t } = useTranslation(['cart', 'common'])
  const router = useRouter()
  const [isNavigatingToDetails, startDetailsTransition] = useTransition()
  const [isNavigatingToCalculator, startCalculatorTransition] = useTransition()
  const { product, externalInsurance, priceIntentData } = recommendation

  const buildPriceCalculatorLink = () => {
    const url = new URL(product.priceCalculatorPageLink, window.location.origin)

    if (priceIntentData != null) {
      url.searchParams.set(PREFILL_FROM_RECOMMENDATION_QUERY_PARAM, '1')
    }

    return appendPriceIntentAttributionQuery(`${url.pathname}${url.search}`, {
      userFlow: EXTERNAL_INSURANCE_PRODUCT_RECOMMENDATION_USER_FLOW,
    })
  }

  return (
    <RecommendationCard
      recommendation={recommendation}
      isExpanded={externalIsExpanded}
      defaultExpanded={defaultExpanded}
      onExpandedChange={onExpandedChange}
    >
      <div className={yStack({ gap: 'md' })}>
        <Card.Header>
          <div className={xStack({ gap: 'sm', alignItems: 'flex-start' })}>
            <Card.Media>
              <HedvigPillow size="small" alt={product.pillowImage.alt} />
            </Card.Media>
            <Card.Heading>
              <Card.Title>{product.displayNameFull}</Card.Title>
              {externalInsurance.insuredObject && <Card.Subtitle>{externalInsurance.insuredObject}</Card.Subtitle>}
            </Card.Heading>
          </div>
        </Card.Header>

        <Alert.Root variant="info">
          <Alert.Icon icon={InfoIcon} />
          <Alert.Body>
            <Alert.Message>{t('RECOMMENDED_PRODUCT_NEED_MORE_INFO_FOR_PRICE', { ns: 'cart' })}</Alert.Message>
          </Alert.Body>
        </Alert.Root>

        <footer className={xStack({ gap: 'xs' })}>
          <Button
            variant="secondary"
            size="medium"
            fullWidth
            loading={isNavigatingToDetails}
            onClick={() =>
              startDetailsTransition(() =>
                router.push(
                  appendPriceIntentAttributionQuery(product.pageLink, {
                    userFlow: EXTERNAL_INSURANCE_PRODUCT_RECOMMENDATION_USER_FLOW,
                  }),
                ),
              )
            }
          >
            {t('READ_MORE', { ns: 'common' })}
          </Button>
          <Button
            size="medium"
            fullWidth
            loading={isNavigatingToCalculator}
            onClick={() => startCalculatorTransition(() => router.push(buildPriceCalculatorLink()))}
          >
            {t('GET_PRICE_LINK', { ns: 'common' })}
          </Button>
        </footer>
      </div>
    </RecommendationCard>
  )
}
